/* eslint camelcase: "off" */
/**
 * Documents audit — compliance / engineering surface over all stored
 * verification documents.
 *
 * Wired to verificationClient.listDocumentsAudit (django-monolith
 * /verification/admin/documents-audit/).
 *
 * Surfaces:
 *  * Retention horizon — what's coming up for crypto-shred + delete
 *  * Legal holds — documents the sweeper exempts (set out-of-band via
 *    Django admin / management command; no write surface here yet)
 *  * Access counts per document (30d window) — annotated server-side
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  InputField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { Eye, Loader2, ScanLine, Shield } from 'lucide-react';

import apiResponseHandler from '../../../../../utils/apiResponseHandler';
import {
  DocumentAuditItem,
  DocumentRetentionStatus,
  DocumentsAuditStats,
  IdExtractionResponse,
  verificationClient,
} from '../../../../../services/verificationClient';
import {
  IdExtractionResultCard,
  type IdCascadeResult,
} from '../extractionResults';


function daysUntil(iso: string): number {
  // Math.round flips around the noon boundary — a row with a few hours
  // left can show "0d" and a row with 30d 10h can show "30d", making the
  // 30-day retention warning misleading. Ceil future dates and floor
  // overdue ones so "Xd left" never under-counts the remaining window.
  const days = (new Date(iso).getTime() - Date.now()) / (86400 * 1000);
  return days >= 0 ? Math.ceil(days) : Math.floor(days);
}


const DocumentsAuditTab: React.FC = () => {
  const requestIdRef = useRef(0);
  const [search, setSearch] = useState('');
  const [retentionFilter, setRetentionFilter] = useState<string>('_all');
  const [kindFilter, setKindFilter] = useState<string>('_all');
  const [rows, setRows] = useState<DocumentAuditItem[]>([]);
  const [stats, setStats] = useState<DocumentsAuditStats | null>(null);
  const [loading, setLoading] = useState(false);

  // Image reveal — same flow as SessionDetailPage. `revealedImages` is the
  // docId → data: URL map of decrypted bytes, populated after the reason
  // gate clears. Kept only for the lifetime of this tab render. Image
  // renders inline in an expanded row below the doc once revealed.
  const [revealedImages, setRevealedImages] = useState<Record<string, string>>({});
  // The same reason modal serves two actions: reveal (decrypted bytes) and
  // extract (re-run the analyzer cascade). The doc itself is the same,
  // but each writes its own DocumentAccessLog row, so each needs its own
  // reason from the admin.
  const [showReasonModal, setShowReasonModal] = useState<
    { docId: string; action: 'reveal' | 'extract' } | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [ticketRef, setTicketRef] = useState('');

  // Per-doc extraction state — populated after a successful re-extract.
  // Result renders inline under the image; admin sees the unmasked
  // extracted ID number + decoded fields, since the access is audited
  // via the download step.
  const [extractions, setExtractions] = useState<Record<string, IdExtractionResponse>>({});

  const closeReasonModal = useCallback(() => {
    setShowReasonModal(null);
    setReason('');
    setTicketRef('');
    setSubmitError(null);
  }, []);

  const confirmReason = useCallback(async () => {
    if (!showReasonModal || !reason.trim() || submitting) return;
    const { docId, action } = showReasonModal;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (action === 'reveal') {
        const dataUrl = await verificationClient.getDocumentImageDataUrl(docId, {
          reason: reason.trim(),
          ticketRef: ticketRef.trim() || undefined,
        });
        setRevealedImages((prev) => ({ ...prev, [docId]: dataUrl }));
      } else {
        const result = await verificationClient.runDocumentIdExtraction(docId, {
          reason: reason.trim(),
          ticketRef: ticketRef.trim() || undefined,
        });
        setExtractions((prev) => ({ ...prev, [docId]: result }));
      }
      closeReasonModal();
    } catch (err) {
      const detail = (
        err as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      const fallback =
        action === 'reveal' ? 'Failed to reveal image.' : 'Failed to run extraction.';
      setSubmitError(detail || `${fallback} Please try again.`);
    } finally {
      setSubmitting(false);
    }
  }, [showReasonModal, reason, ticketRef, submitting, closeReasonModal]);

  const load = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    try {
      const params: Parameters<typeof verificationClient.listDocumentsAudit>[0] = {};
      if (kindFilter !== '_all') {
        if (kindFilter === 'sa_id' || kindFilter === 'passport') {
          params.document_kind = kindFilter;
        } else {
          // 'id_photo' / 'selfie' are doc_type values, not document_kind.
          params.doc_types = kindFilter;
        }
      }
      if (retentionFilter !== '_all') {
        params.retention_status = mapRetentionFilter(retentionFilter);
      }
      // `search` is server-side optional — backend currently doesn't
      // accept a free-text search on documents-audit. Filter the page
      // client-side and let the user know if needed.
      const page = await verificationClient.listDocumentsAudit(params);
      if (requestId !== requestIdRef.current) return;
      setRows(page.results);
      setStats(page.stats);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      apiResponseHandler.handleApiError(err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [kindFilter, retentionFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // The backend already applies retention + kind filters; `search` is
  // client-side because the API doesn't accept a free-text search yet.
  const display: DocumentAuditItem[] = useMemo(() => {
    if (!search) return rows;
    const needle = search.toLowerCase();
    return rows.filter(
      (d) =>
        d.id.toLowerCase().includes(needle) ||
        String(d.employee_id).includes(needle) ||
        (d.employee_name || '').toLowerCase().includes(needle)
    );
  }, [rows, search]);

  const displayStats: DocumentsAuditStats = stats || {
    total: 0,
    on_hold: 0,
    due_within_30d: 0,
    purged: 0,
  };

  return (
    <div>
      {/* Stats bar */}
      <div className="mb-sm grid grid-cols-2 gap-sm md:grid-cols-4">
        <StatCard label="Total documents" value={displayStats.total} />
        <StatCard label="On legal hold" value={displayStats.on_hold} accent="warning" />
        <StatCard
          label="Due within 30d"
          value={displayStats.due_within_30d}
          accent="warning"
        />
        <StatCard label="Purged (shredded)" value={displayStats.purged} />
      </div>

      {/* Filters */}
      <div className="mb-sm grid grid-cols-1 gap-sm rounded-2xl border border-greyscale-border-default bg-white p-md md:grid-cols-3">
        <InputField
          label="Search"
          value={search}
          placeholder="Document ID, employee"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        />
        <div className="flex flex-col gap-xxs">
          <span className="text-xs text-greyscale-text-caption">Retention</span>
          <Select value={retentionFilter} onValueChange={setRetentionFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="within_30d">Expiring within 30 days</SelectItem>
              <SelectItem value="on_hold">On legal hold</SelectItem>
              <SelectItem value="purged">Already purged</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-xxs">
          <span className="text-xs text-greyscale-text-caption">Type</span>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Any</SelectItem>
              <SelectItem value="id_photo">ID photo</SelectItem>
              <SelectItem value="selfie">Selfie</SelectItem>
              <SelectItem value="sa_id">SA ID specifically</SelectItem>
              <SelectItem value="passport">Passport specifically</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-greyscale-border-default bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document ID</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Captured</TableHead>
              <TableHead>Retention</TableHead>
              <TableHead>Access (30d)</TableHead>
              <TableHead>Size</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {display.map((d) => {
              const days = daysUntil(d.retention_expires_at);
              const imageDataUrl = revealedImages[d.id];
              return (
                <React.Fragment key={d.id}>
                <TableRow>
                  <TableCell>
                    <code className="text-xs">{shortenUuid(d.id)}</code>
                    {d.has_legal_hold && (
                      <div
                        className="mt-xxs flex items-center gap-xxs text-xxs text-warning-text-label"
                        title={d.legal_hold_reason || 'Legal hold active'}
                      >
                        <Shield size={10} /> legal hold
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">{d.employee_name || '—'}</div>
                    <div className="text-xxs text-greyscale-text-caption">
                      #{d.employee_id}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {d.document_kind || d.doc_type}
                  </TableCell>
                  <TableCell className="text-xs">
                    {new Date(d.captured_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {d.purged_at ? (
                      <Tag variant="success">purged</Tag>
                    ) : d.has_legal_hold ? (
                      <Tag variant="pending">held — sweep skipped</Tag>
                    ) : days < 0 ? (
                      <Tag variant="failed">{Math.abs(days)}d overdue</Tag>
                    ) : days <= 30 ? (
                      <Tag variant="failed">{days}d left</Tag>
                    ) : (
                      <span className="text-xs text-greyscale-text-caption">{days}d</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{d.access_count_30d}</TableCell>
                  <TableCell className="text-xs">
                    {d.size_bytes !== null && d.size_bytes !== undefined ? (
                      `${(d.size_bytes / 1024).toFixed(0)} KB`
                    ) : (
                      <span className="text-greyscale-text-caption">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {d.purged_at ? (
                      <span className="text-xxs text-greyscale-text-caption">purged</span>
                    ) : (
                      <div className="flex flex-col items-end gap-xxs">
                        <Button
                          variant="link"
                          size="xs"
                          leftIcon={<Eye size={14} />}
                          onClick={() => setShowReasonModal({ docId: d.id, action: 'reveal' })}
                          title={imageDataUrl ? 'Re-reveal (writes another audit row)' : 'Reveal image — requires access reason'}
                        >
                          {imageDataUrl ? 're-reveal' : 'view'}
                        </Button>
                        {/* Extract only applies to ID documents (the SA-ID
                            cascade + passport MRZ analyzer). Selfies show
                            the View button only. Each click runs the
                            download endpoint and writes a DocumentAccessLog
                            row of kind=`download`. */}
                        {d.doc_type !== 'selfie' && (
                          <Button
                            variant="link"
                            size="xs"
                            leftIcon={<ScanLine size={14} />}
                            onClick={() => setShowReasonModal({ docId: d.id, action: 'extract' })}
                            title="Run the ID extraction cascade against this document (audited)"
                          >
                            extract
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
                {imageDataUrl && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-greyscale-surface-subtle">
                      <div className="flex flex-col items-center gap-xs p-xs">
                        <img
                          src={imageDataUrl}
                          alt={`${d.document_kind || d.doc_type} for employee ${d.employee_id}`}
                          className="max-h-80 max-w-full rounded-lg border border-greyscale-border-subtle object-contain"
                        />
                        <div className="text-xxs text-greyscale-text-caption">
                          Decrypted for this view only — a DocumentAccessLog row was written with your name, reason, and timestamp.
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {extractions[d.id] && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-greyscale-surface-subtle">
                      <div className="flex flex-col gap-xs p-xs">
                        <div className="flex items-center justify-between text-xxs text-greyscale-text-caption">
                          <span>
                            Extraction cascade — {extractions[d.id].results.length} kind(s) tried,{' '}
                            {extractions[d.id].latency_ms} ms. Values are unmasked because the
                            download access was just audited.
                          </span>
                          <Button
                            variant="link"
                            size="xs"
                            onClick={() =>
                              setExtractions((prev) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const { [d.id]: _omit, ...next } = prev;
                                return next;
                              })
                            }
                          >
                            clear
                          </Button>
                        </div>
                        {extractions[d.id].results.map((r) => (
                          <IdExtractionResultCard
                            key={r.document_kind}
                            result={r as IdCascadeResult}
                            showFull
                          />
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              );
            })}
            {display.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-md text-center text-greyscale-text-caption">
                  No documents match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="flex justify-center py-sm">
            <Loader2 className="animate-spin text-secondary-text-label" size={20} />
          </div>
        )}
      </div>

      {/* Reason modal — same shape as the SessionDetailPage reveal flow,
          generalised for both reveal and extract actions. Each writes its
          own DocumentAccessLog row server-side, both kind=download: reveal
          fetches decrypted bytes directly, and the same download endpoint
          is step 1 of the extract pipeline. */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-md">
            <h4 className="text-sm font-semibold mb-xs">
              {showReasonModal.action === 'reveal'
                ? 'Reason for viewing image'
                : 'Reason for running extraction'}
            </h4>
            <p className="text-xs text-greyscale-text-caption mb-sm">
              {showReasonModal.action === 'reveal'
                ? 'A DocumentAccessLog row will be written (kind=download) with your name, the time, and this reason. The image is decrypted server-side and returned inline, then held in memory for this view only.'
                : 'A DocumentAccessLog row will be written (kind=download). The bytes are decrypted server-side and the analyzer cascade (barcode + MRZ + OCR) runs against them; nothing extra is persisted.'}
            </p>
            <div className="flex flex-col gap-sm">
              <InputField
                label="Reason (required)"
                value={reason}
                placeholder="Reviewing cluster-001 / SAR-018 / etc."
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
              />
              <InputField
                label="Ticket ref (optional)"
                value={ticketRef}
                placeholder="INC-1234"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketRef(e.target.value)}
              />
            </div>
            {submitError && (
              <div className="mt-xs rounded-md border border-error-border-default bg-error-surface-subtle p-xs text-xs text-error-text-label">
                {submitError}
              </div>
            )}
            <div className="mt-sm flex justify-end gap-xxs">
              <Button
                variant="secondary"
                size="sm"
                disabled={submitting}
                onClick={closeReasonModal}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!reason.trim() || submitting}
                onClick={confirmReason}
              >
                {submitting && <Loader2 className="animate-spin" size={14} />}
                {!submitting && (showReasonModal.action === 'reveal' ? 'Reveal image' : 'Run extraction')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Mock UI's retention filter uses different value labels than the
// backend's `retention_status` enum. Translate here so the UI surface
// stays stable.
function mapRetentionFilter(value: string): DocumentRetentionStatus | undefined {
  if (value === 'within_30d') return 'expiring_30d';
  if (value === 'on_hold') return 'held';
  if (value === 'purged') return 'purged';
  return undefined;
}


function shortenUuid(uuid: string): string {
  // First 8 + last 4 — readable, doesn't break table column widths.
  if (uuid.length < 14) return uuid;
  return `${uuid.slice(0, 8)}…${uuid.slice(-4)}`;
}


function StatCard({
  label,
  value,
  accent,
  footer,
}: {
  label: string;
  value: number;
  accent?: 'warning';
  footer?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-2xl border p-md ${
        accent === 'warning'
          ? 'border-warning-border-default bg-warning-surface-subtle'
          : 'border-greyscale-border-default bg-white'
      }`}
    >
      <div className="text-xs text-greyscale-text-caption uppercase tracking-wide">{label}</div>
      <div className="mt-xxs text-2xl font-semibold">{value}</div>
      {footer && <div className="mt-xxs">{footer}</div>}
    </div>
  );
}

export default DocumentsAuditTab;
